import { useEffect, useRef, useState, useCallback } from 'react'
import { Observable, Subscription } from 'rxjs'
import { requestTracker, generateRequestId } from './useRequestTracker'

interface UseTrackedApiOptions<T> {
  onSuccess?: (data: T) => void
  onError?: (error: any) => void
  skip?: boolean
  deps?: any[]
  trackRequest?: boolean // Whether to track this request for page reload prevention
}

export function useTrackedApi<T>(
  apiCall: () => Observable<any>,
  options: UseTrackedApiOptions<T> = {},
) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const subscriptionRef = useRef<Subscription | null>(null)
  const isMountedRef = useRef(true)
  const isFetchingRef = useRef(false)
  const requestIdRef = useRef(0)
  const trackedRequestIdRef = useRef<string | null>(null)

  // Memoize the API call function
  const memoizedApiCall = useCallback(apiCall, options.deps || [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (options.skip) return

    const currentRequestId = ++requestIdRef.current

    const fetchData = () => {
      if (!isMountedRef.current || currentRequestId !== requestIdRef.current)
        return

      isFetchingRef.current = true
      setLoading(true)
      setError(null)

      // Track request if enabled
      if (options.trackRequest !== false) {
        trackedRequestIdRef.current = generateRequestId()
        requestTracker.addRequest(trackedRequestIdRef.current)
      }

      // Cleanup previous subscription if it exists
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }

      subscriptionRef.current = memoizedApiCall().subscribe({
        next: (response) => {
          if (
            !isMountedRef.current ||
            currentRequestId !== requestIdRef.current
          )
            return

          if (!response || !response.response) {
            throw new Error('No response received')
          }

          const responseData = response.response as T
          setData(responseData)
          setLoading(false)
          isFetchingRef.current = false
          options.onSuccess?.(responseData)
        },
        error: (err) => {
          if (
            !isMountedRef.current ||
            currentRequestId !== requestIdRef.current
          )
            return

          console.error('API Error:', err)
          setError(err instanceof Error ? err.message : 'An error occurred')
          setLoading(false)
          isFetchingRef.current = false
          options.onError?.(err)
        },
        complete: () => {
          if (
            !isMountedRef.current ||
            currentRequestId !== requestIdRef.current
          )
            return
          isFetchingRef.current = false
        },
      })
    }

    fetchData()

    return () => {
      if (currentRequestId === requestIdRef.current) {
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe()
          subscriptionRef.current = null
        }
        isFetchingRef.current = false

        // Remove tracked request
        if (trackedRequestIdRef.current) {
          requestTracker.removeRequest(trackedRequestIdRef.current)
          trackedRequestIdRef.current = null
        }
      }
    }
  }, [memoizedApiCall, options.skip, options.trackRequest])

  return { data, loading, error }
}
